import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import { AnswerID } from '@aytm/app/swidget/base/questions/types'
import { EcommerceBannerPlacement, EcommerceResponse, IEcommerceQuestionSchema } from '../../types.ts'
import BannerAd from './BannerAd.tsx'
import ProductCard from './ProductCard.tsx'
import SponsoredCarousel from './SponsoredCarousel.tsx'
import style from './Main.module.scss'

interface IMainProps {
  schema: IEcommerceQuestionSchema
  response: EcommerceResponse
  findability: boolean
  cartTotal: number
  registerCardRef: (id: AnswerID, element: HTMLDivElement | null) => void
  onItemSelect: (index: number, bannerPlacement?: EcommerceBannerPlacement) => void
  onAddToCart: (index: number, qnt: number) => void
  scrollTop: () => void
}

// Decorative filter chips — no real filtering in the sim.
const FILTERS = [
  'filterSort',
  'filterPriceRange',
  'filterSavings',
  'filterBrands',
  'filterAvailability',
  'filterWaysToShop',
  'filterDepartments',
  'filterDietary',
  'filterSnapEbt'
]

const computeDefaultColumns = (width: number) => {
  if (width > 1920) return 7
  if (width > 1720) return 6
  if (width > 1440) return 5
  return 4
}

const Main = ({
  schema,
  response,
  findability,
  cartTotal,
  registerCardRef,
  onItemSelect,
  onAddToCart,
  scrollTop
}: IMainProps) => {
  const { t } = useSurveyLocale()
  const { width, mobile } = useViewport()

  const columns = mobile ? 1 : schema.products_per_row || computeDefaultColumns(width)
  const topLevel = schema.answers.filter(answer => !answer.na && !answer.parent_product)
  // A banner-flagged product is promoted into the band and leaves the grid, so it is absent
  // from the cards and from the result count the header reports.
  const bannerProducts = topLevel.filter(answer => answer.banner)
  const products = topLevel.filter(answer => !answer.banner)
  // The banner is the grid's second row, so it splits the products after exactly one row.
  const productsBeforeBanner = products.slice(0, columns)
  const productsAfterBanner = products.slice(columns)

  return (
    <section
      className={`${style.main} ${mobile ? style.mainMobile : ''} ${
        cartTotal > 0 && !findability && !mobile ? style.openRail : ''
      }`}
    >
      <div className={style.content}>
        <div className={style.searchBar}>
          <div className={style.resultsHeader}>
            {schema.search_condition && (
              <span className={style.resultsTitle} dangerouslySetInnerHTML={{ __html: schema.search_condition }} />
            )}
            <span className={style.resultsCount}>
              {t<(count: number) => string>('questions', 'ec', 'kroger', 'results')(products.length)}
            </span>
          </div>
          <div className={style.filters}>
            {FILTERS.map(key => (
              <EmptyLink key={key} color="#0e51a1">
                <span className={style.filterChip}>
                  {t('questions', 'ec', 'kroger', key)}
                  <i className="pi pi-chevron-down" />
                </span>
              </EmptyLink>
            ))}
          </div>
        </div>

        <SponsoredCarousel
          schema={schema}
          response={response}
          findability={findability}
          onItemSelect={onItemSelect}
          onAddToCart={onAddToCart}
        />

        <div className={style.grid} style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
          {productsBeforeBanner.map(answer => (
            <ProductCard
              key={answer.id}
              data-testid="SW-EC-Item"
              schema={schema}
              answer={answer}
              amount={response.answers[answer.index].v}
              findability={findability}
              disabled={'focus' == schema.mode_type && !answer.focused}
              registerCardRef={registerCardRef}
              onSelect={() => onItemSelect(answer.index)}
              onAdd={qnt => onAddToCart(answer.index, qnt)}
            />
          ))}

          {bannerProducts.length > 0 && (
            <BannerAd
              schema={schema}
              bannerProducts={bannerProducts}
              response={response}
              findability={findability}
              onItemSelect={onItemSelect}
              onAddToCart={onAddToCart}
            />
          )}

          {productsAfterBanner.map(answer => (
            <ProductCard
              key={answer.id}
              data-testid="SW-EC-Item"
              schema={schema}
              answer={answer}
              amount={response.answers[answer.index].v}
              findability={findability}
              disabled={'focus' == schema.mode_type && !answer.focused}
              registerCardRef={registerCardRef}
              onSelect={() => onItemSelect(answer.index)}
              onAdd={qnt => onAddToCart(answer.index, qnt)}
            />
          ))}
        </div>

        <div className={style.backToTop}>
          <button type="button" className={style.backToTopButton} onClick={scrollTop}>
            {t('questions', 'ec', 'kroger', 'backToTop')}
          </button>
        </div>
      </div>
    </section>
  )
}

export default Main
